import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getAllApps,
  getAllSourcePlatforms,
  getAppAccessByCustomerId,
  createAppAccess,
  updateAppAccess,
  getIntegrationAccessByCustomerId,
  createIntegrationAccess,
  updateIntegrationAccess,
  getCompanyById,
  updateCompany,
  getComplianceFrameworkAccessByCustomerId,
  upsertComplianceFrameworkAccess,
  updateComplianceFrameworkAccess,
  getHackNoticeWatchlist,
  addHackNoticeWatchlistEmail,
} from "../../utils/api";
import TabButton from "./TabButton";

const AccessManagementModal = ({ isOpen, onClose, companyId, companyName }) => {
  const [activeTab, setActiveTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [sourcePlatforms, setSourcePlatforms] = useState([]);
  const [appAccess, setAppAccess] = useState({});
  const [integrationAccess, setIntegrationAccess] = useState({});
  const [complianceFrameworkAccess, setComplianceFrameworkAccess] = useState([]);
  const [isWhiteLabeled, setIsWhiteLabeled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [watchlistEmails, setWatchlistEmails] = useState([]);
  const [newWatchlistEmail, setNewWatchlistEmail] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        appsData,
        platformsData,
        appAccessData,
        integrationAccessData,
        companyData,
        complianceAccessData,
        watchlistData,
      ] = await Promise.all([
        getAllApps(),
        getAllSourcePlatforms(),
        getAppAccessByCustomerId(companyId),
        getIntegrationAccessByCustomerId(companyId),
        getCompanyById(companyId),
        getComplianceFrameworkAccessByCustomerId(companyId),
        getHackNoticeWatchlist(companyId),
      ]);

      setApps(appsData);
      setSourcePlatforms(platformsData);
      if (companyData) {
        setIsWhiteLabeled(companyData.is_white_labeled || false);
      }

      // Map access data for easier lookup
      const appAccessMap = {};
      appAccessData.forEach((access) => {
        appAccessMap[access.app_id] = access;
      });
      setAppAccess(appAccessMap);

      const integrationAccessMap = {};
      integrationAccessData.forEach((access) => {
        integrationAccessMap[access.source_platform_id] = access;
      });
      setIntegrationAccess(integrationAccessMap);
      setComplianceFrameworkAccess(complianceAccessData);
      setWatchlistEmails(watchlistData);
    } catch (error) {
      console.error("Error fetching access data:", error);
      toast.error("Failed to load access data");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (isOpen && companyId) {
      setActiveTab("apps"); // Reset to apps tab when modal opens
      fetchData();
    }
  }, [isOpen, companyId, fetchData]);

  const handleAppToggle = async (appId, newStatus) => {
    const currentAccess = appAccess[appId];

    try {
      let success = false;
      if (currentAccess) {
        // Update existing access
        success = await updateAppAccess(currentAccess.id, {
          is_enabled: newStatus,
        });
      } else {
        // Create new access
        success = await createAppAccess({
          company_id: companyId,
          app_id: appId,
          is_enabled: newStatus,
        });
      }

      if (success) {
        toast.success(`App access ${newStatus ? "enabled" : "disabled"}`);
        // Refresh access data to get new IDs if created
        const updatedAccessData = await getAppAccessByCustomerId(companyId);
        const appAccessMap = {};
        updatedAccessData.forEach((access) => {
          appAccessMap[access.app_id] = access;
        });
        setAppAccess(appAccessMap);
      } else {
        toast.error("Failed to update app access");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating app access");
    }
  };

  const handleIntegrationToggle = async (platformId, newStatus) => {
    const currentAccess = integrationAccess[platformId];

    try {
      let success = false;
      if (currentAccess) {
        // Update existing access
        success = await updateIntegrationAccess(currentAccess.id, {
          is_enabled: newStatus,
        });
      } else {
        // Create new access
        success = await createIntegrationAccess({
          company_id: companyId,
          source_platform_id: platformId,
          is_enabled: newStatus,
        });
      }

      if (success) {
        toast.success(
          `Integration access ${newStatus ? "enabled" : "disabled"}`
        );
        // Refresh access data
        const updatedAccessData = await getIntegrationAccessByCustomerId(
          companyId
        );
        const integrationAccessMap = {};
        updatedAccessData.forEach((access) => {
          integrationAccessMap[access.source_platform_id] = access;
        });
        setIntegrationAccess(integrationAccessMap);
      } else {
        toast.error("Failed to update integration access");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating integration access");
    }
  };

  const handleComplianceFrameworkToggle = async (frameworkType, currentRecord, newStatus) => {
    try {
      let success = false;
      if (currentRecord.id) {
        success = await updateComplianceFrameworkAccess(currentRecord.id, { is_enabled: newStatus });
      } else {
        success = await upsertComplianceFrameworkAccess({
          company_id: companyId,
          framework_type: frameworkType,
          is_enabled: newStatus,
        });
      }

      if (success) {
        toast.success(`${frameworkType.replace(/_/g, ' ')} ${newStatus ? "enabled" : "disabled"}`);
        const updatedData = await getComplianceFrameworkAccessByCustomerId(companyId);
        setComplianceFrameworkAccess(updatedData);
      } else {
        toast.error("Failed to update compliance framework access");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating compliance framework access");
    }
  };

  const handleAddWatchlistEmail = async () => {
    const trimmed = newWatchlistEmail.trim();
    if (!trimmed) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const success = await addHackNoticeWatchlistEmail(companyId, trimmed);
      if (success) {
        toast.success("Email added to watchlist");
        setNewWatchlistEmail("");
        const updated = await getHackNoticeWatchlist(companyId);
        setWatchlistEmails(updated);
      } else {
        toast.error("Failed to add email to watchlist");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error adding email to watchlist");
    }
  };

  const handleWhitelabelToggle = async () => {
    const newStatus = !isWhiteLabeled;
    try {
      const success = await updateCompany(companyId, {
        is_white_labeled: newStatus,
      });

      if (success) {
        setIsWhiteLabeled(newStatus);
        toast.success(`Whitelabel ${newStatus ? "enabled" : "disabled"}`);
      } else {
        toast.error("Failed to update whitelabel status");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating whitelabel status");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="bg-white p-5 rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Manage Access for {companyName}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-4 flex items-center justify-between bg-gray-50 p-3 rounded">
          <span className="font-medium text-gray-700">Custom Branding</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isWhiteLabeled}
              onChange={handleWhitelabelToggle}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="tabs border-b border-gray-300 pb-2 mb-4">
          <TabButton
            selectedTab={activeTab}
            handleTabChange={setActiveTab}
            tabName="apps"
          >
            App Access
          </TabButton>
          <TabButton
            selectedTab={activeTab}
            handleTabChange={setActiveTab}
            tabName="integrations"
          >
            Integration Access
          </TabButton>
          <TabButton
            selectedTab={activeTab}
            handleTabChange={setActiveTab}
            tabName="compliance"
          >
            Compliance Frameworks
          </TabButton>
          <TabButton
            selectedTab={activeTab}
            handleTabChange={setActiveTab}
            tabName="hacknotice"
          >
            HackNotice Watchlist
          </TabButton>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : activeTab === "hacknotice" ? (
          <div className="max-h-96 overflow-y-auto">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">
                Add Email to HackNotice Watchlist
              </h3>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={newWatchlistEmail}
                  onChange={(e) => setNewWatchlistEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddWatchlistEmail()}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                <button
                  onClick={handleAddWatchlistEmail}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Add Email
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Emails added here will be monitored for breaches and leaks.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Monitored Emails</h3>
              {watchlistEmails.length === 0 ? (
                <p className="text-gray-500 text-sm py-4">
                  No emails currently monitored.
                </p>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="py-2 px-4 border-b">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {watchlistEmails.map((email, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="py-3 px-4 border-b">{email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Name</th>
                  <th className="py-2 px-4 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeTab === "apps"
                  ? apps.map((app) => {
                      const access = appAccess[app.id];
                      // No per-company row yet → show the app's own default
                      // (apps.default_enabled, set from the Apps tab's
                      // "Off for All"). This used to guess from the app's
                      // name, which drifted from what the backend enforces.
                      const defaultVal = app.default_enabled !== false;
                      const isEnabled = access ? access.is_enabled : defaultVal;
                      return (
                        <tr key={app.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b">{app.name}</td>
                          <td className="py-3 px-4 border-b">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={isEnabled}
                                onChange={() => handleAppToggle(app.id, !isEnabled)}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </td>
                        </tr>
                      );
                    })
                  : activeTab === "integrations"
                  ? sourcePlatforms.map((platform) => {
                      const access = integrationAccess[platform.id];
                      const defaultOffPlatforms = ["hack_notice", "hacknotice"];
                      const defaultVal = defaultOffPlatforms.includes(platform.name?.toLowerCase()) || defaultOffPlatforms.includes(platform.code?.toLowerCase()) ? false : true;
                      const isEnabled = access ? access.is_enabled : defaultVal;
                      return (
                        <tr key={platform.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b">{platform.name}</td>
                          <td className="py-3 px-4 border-b">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={isEnabled}
                                onChange={() => handleIntegrationToggle(platform.id, !isEnabled)}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </td>
                        </tr>
                      );
                    })
                  : complianceFrameworkAccess.map((record) => {
                      const label = record.framework_type.replace(/_/g, ' ')
                      return (
                        <tr key={record.framework_type} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b font-medium">{label}</td>
                          <td className="py-3 px-4 border-b">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={record.is_enabled}
                                onChange={() => handleComplianceFrameworkToggle(record.framework_type, record, !record.is_enabled)}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessManagementModal;
